import {
    FC,
    ReactElement,
    useEffect,
    useState,
} from 'react'
import { useNavigate } from 'react-router'

import { LoadingSpinner, ZeroState } from '@/components'
import { IconButton, ModalContent, PacoModal, ShiftsListItem, TabButtons } from '@/compositions'
import { ConnectedDateNavigator, ConnectedDownloadShiftsIcalForm } from '@/connectors'
import { MY_SHIFTS, OPEN_SHIFTS, ShiftsTab } from '@/constants'
import { trans } from '@/helpers/trans'
import { Shift } from '@/models'
import { getExchangesOfferedForMe, getShiftExchanges } from '@/redux/exchanges/exchangesActions'
import { getOpenShifts, getPlannedShifts, getShiftsTabLink } from '@/redux/shifts/shiftsLegacyActions'
import { useTypedDispatch, useTypedSelector } from '@/redux/store'

import './ShiftsPage.scss'

interface ShiftsPageProps {
    tab: ShiftsTab
}

export const ShiftsPage: FC<ShiftsPageProps> = ({ tab }): ReactElement => {
    const dispatch = useTypedDispatch()
    const navigate = useNavigate()
    const {
        plannedShifts,
        openShifts,
        loading: isShiftsLoading,
    } = useTypedSelector(state => state.shiftsReducer)
    const { shiftsOfferedForMe, loading: isExchangesLoading } = useTypedSelector(state => state.exchangesReducer)
    const { currentUser, settings } = useTypedSelector(state => state.userReducer)
    const [isDownloadShiftsIcalModalOpen, setIsDownloadShiftsIcalModalOpen] = useState(false)

    const fetchShifts = (): void => {
        if (tab === MY_SHIFTS) {
            dispatch(getPlannedShifts())

            return
        }

        dispatch(getOpenShifts())
        dispatch(getShiftExchanges())
        dispatch(getExchangesOfferedForMe())
    }

    useEffect(fetchShifts, [tab])

    const handleTabButtonClick = (index: number): void => {
        const newTab = index === 0 ? MY_SHIFTS : OPEN_SHIFTS

        if (newTab !== tab) {
            navigate(getShiftsTabLink(newTab))
        }
    }

    const handlePlannedShiftClick = (shift: Shift): void => {
        navigate(`/${trans('nav.shifts.link')}/${shift.id}`)
    }

    // An open shift that a colleague offered to the user links to the exchange instead of the shift
    const handleOpenShiftClick = (shift: Shift): void => {
        const exchange = shiftsOfferedForMe.find(shiftExchange => shiftExchange.shift.id === shift.id)

        navigate(exchange
            ? `/${trans('nav.shifts.exchange.link')}/${exchange.id}`
            : `/${trans('nav.shifts.link')}/${shift.id}`)
    }

    const handleDownloadShiftsIcalClick = (): void => {
        setIsDownloadShiftsIcalModalOpen(true)
    }

    const handleDownloadShiftsIcalClose = (): void => {
        setIsDownloadShiftsIcalModalOpen(false)
    }

    return (
        <>
            <div className="shifts__calendar-export-wrapper">
                <IconButton
                    hideLabel
                    disabled={! currentUser}
                    text={trans('common.export')}
                    icon="calendar-export"
                    className="shifts__calendar-export"
                    iconClassName="shifts__calendar-export-icon"
                    onClick={handleDownloadShiftsIcalClick}
                />
            </div>
            <TabButtons
                activeIndex={tab === MY_SHIFTS ? 0 : 1}
                buttonLabels={[
                    trans('pages.shifts.myShifts'),
                    trans('pages.shifts.open'),
                ]}
                onButtonClick={handleTabButtonClick}
            />
            <ConnectedDateNavigator onAfterDateChange={fetchShifts} />
            <div>
                {isExchangesLoading || isShiftsLoading ? <LoadingSpinner /> : (
                    <>
                        <div className="shifts" hidden={tab !== MY_SHIFTS}>
                            <div className="shifts__row">
                                <div className="shifts__col">
                                    <ul>
                                        {plannedShifts.length > 0 ? plannedShifts.map(shift => (
                                            <ShiftsListItem
                                                key={shift.id}
                                                currentUser={currentUser}
                                                settings={settings}
                                                shift={shift}
                                                onClick={() => handlePlannedShiftClick(shift)}
                                                className="shifts__list-item"
                                            />
                                        )) : (
                                            <ZeroState
                                                text={trans('pages.shifts.emptyPlannedShifts')}
                                                className="shifts__zero-state"
                                            />
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div hidden={tab !== OPEN_SHIFTS}>
                            <div className="shifts__row">
                                <div className="shifts__col">
                                    <ul>
                                        {openShifts.length > 0 ? openShifts.map(shift => (
                                            <ShiftsListItem
                                                key={shift.id}
                                                currentUser={currentUser}
                                                settings={settings}
                                                shift={shift}
                                                onClick={() => handleOpenShiftClick(shift)}
                                            />
                                        )) : (
                                            <ZeroState
                                                text={trans('pages.shifts.emptyList')}
                                                className="shifts__zero-state"
                                            />
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
            {(currentUser && isDownloadShiftsIcalModalOpen) && (
                <PacoModal>
                    <ModalContent title={trans('containers.forms.downloadShiftsIcalForm.exportShifts')}>
                        <ConnectedDownloadShiftsIcalForm
                            userId={currentUser.id}
                            onClose={handleDownloadShiftsIcalClose}
                        />
                    </ModalContent>
                </PacoModal>
            )}
        </>
    )
}
